import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Leaf, Lock, ShieldCheck, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

// Abstract 3D hydroponic / green fodder render — eye-catching, on-brand
const BG_IMAGE = "https://images.unsplash.com/photo-1574483342046-d3e02b91d28a?auto=format&fit=crop&w=1600&q=80";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [stage, setStage] = useState("credentials"); // 'credentials' | 'mfa' | 'mfa-locked'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmitCredentials = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      if (data?.mfa_required) {
        setChallengeId(data.challenge_id);
        setEmailHint(data.email_hint || "");
        setStage("mfa");
        toast.success("Verification code sent to your email");
      } else {
        // Standard login (no MFA)
        localStorage.setItem("hg_token", data.token);
        await login(email.trim(), password); // refresh auth context
        toast.success("Welcome back");
        navigate("/logs", { replace: true });
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail) || "Login failed";
      // Special case: MFA is enabled but email is broken → offer emergency recovery.
      if (status === 503 && /MFA is enabled/i.test(detail)) {
        setStage("mfa-locked");
        setError("");
      } else {
        setError(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onEmergencyDisableMfa = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/emergency-disable-mfa", { email: email.trim(), password });
      toast.success("MFA disabled — signing you in…");
      // Retry the normal login flow now that MFA is off.
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      localStorage.setItem("hg_token", data.token);
      await login(email.trim(), password);
      navigate("/logs", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not disable MFA");
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitMfa = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/auth/verify-mfa", { challenge_id: challengeId, code: code.trim() });
      localStorage.setItem("hg_token", data.token);
      // trigger AuthProvider re-check by calling its login flow via API state — simplest: reload
      window.location.replace("/logs");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Code verification failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG_IMAGE})` }} aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-emerald-950/70 to-slate-900/85" aria-hidden />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8 text-white">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-700/95 mb-4 shadow-lg shadow-emerald-950/50 ring-1 ring-emerald-500/40">
            <Leaf size={28} weight="duotone" color="white" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">The NordHim Ledger</h1>
          <p className="text-emerald-300/90 mt-1 text-[11px] uppercase tracking-[0.3em] font-semibold">TNL · by Sukesh Barua</p>
          <p className="text-slate-200 mt-3 text-sm tracking-wide">Swedish hydroponic sales · Moms &amp; Bolagsskatt</p>
        </div>

        <Card className="border-slate-200 shadow-2xl shadow-slate-900/40">
          <CardContent className="p-6 md:p-8">
            {stage === "credentials" && (
              <>
                <div className="flex items-center gap-2 mb-6">
                  <Lock size={20} weight="duotone" className="text-emerald-800" />
                  <h2 className="font-heading text-lg font-semibold text-slate-900">Admin Sign In</h2>
                </div>
                <form onSubmit={onSubmitCredentials} className="space-y-4" data-testid="login-form">
                  <div>
                    <Label htmlFor="email" className="text-slate-700">Email or Username</Label>
                    <Input id="email" type="text" placeholder="you@example.com or username"
                      value={email} onChange={(e) => setEmail(e.target.value)} required
                      data-testid="login-email-input" className="mt-1.5" autoComplete="email" />
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-slate-700">Password</Label>
                    <Input id="password" type="password" placeholder="••••••••"
                      value={password} onChange={(e) => setPassword(e.target.value)} required
                      data-testid="login-password-input" className="mt-1.5" autoComplete="current-password" />
                  </div>
                  {error && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error-message">{error}</div>
                  )}
                  <Button type="submit" className="w-full bg-emerald-800 hover:bg-emerald-900 text-white" disabled={submitting} data-testid="login-submit-button">
                    {submitting ? "Signing in…" : "Sign in"}
                  </Button>
                </form>
              </>
            )}

            {stage === "mfa-locked" && (
              <>
                <button
                  type="button"
                  onClick={() => { setStage("credentials"); setError(""); }}
                  className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3"
                  data-testid="mfa-locked-back"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck size={20} weight="duotone" className="text-red-700" />
                  <h2 className="font-heading text-lg font-semibold text-slate-900">MFA is broken — recover access</h2>
                </div>
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-900 mb-4 space-y-1">
                  <p><strong>Sign-in is blocked because MFA is enabled but the email service is not configured.</strong></p>
                  <p>As master admin you can disable MFA using your password. Re-enable it from Settings after fixing Resend (add a valid API key and verify a from-domain).</p>
                </div>
                <form onSubmit={onEmergencyDisableMfa} className="space-y-4" data-testid="mfa-locked-form">
                  <div>
                    <Label className="text-slate-700">Master admin email</Label>
                    <Input value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" data-testid="mfa-locked-email" />
                  </div>
                  <div>
                    <Label className="text-slate-700">Master password</Label>
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" data-testid="mfa-locked-password" />
                  </div>
                  {error && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="mfa-locked-error">{error}</div>
                  )}
                  <Button type="submit" className="w-full bg-red-700 hover:bg-red-800 text-white" disabled={submitting} data-testid="mfa-locked-disable">
                    {submitting ? "Disabling MFA…" : "Disable MFA & sign in"}
                  </Button>
                </form>
              </>
            )}

            {stage === "mfa" && (
              <>
                <button
                  type="button"
                  onClick={() => { setStage("credentials"); setCode(""); setError(""); }}
                  className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3"
                  data-testid="mfa-back-button"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck size={20} weight="duotone" className="text-emerald-800" />
                  <h2 className="font-heading text-lg font-semibold text-slate-900">Verify it's you</h2>
                </div>
                <p className="text-sm text-slate-600 mb-5">We sent a 6-digit code to <span className="font-medium text-slate-900">{emailHint || "your email"}</span>. It expires in 10 minutes.</p>
                <form onSubmit={onSubmitMfa} className="space-y-4" data-testid="mfa-form">
                  <div>
                    <Label htmlFor="code" className="text-slate-700">Verification Code</Label>
                    <Input
                      id="code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="123456"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      required
                      data-testid="mfa-code-input"
                      className="mt-1.5 text-center text-2xl tracking-[0.5em] font-heading"
                    />
                  </div>
                  {error && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="mfa-error-message">{error}</div>
                  )}
                  <Button type="submit" className="w-full bg-emerald-800 hover:bg-emerald-900 text-white" disabled={submitting || code.length < 4} data-testid="mfa-verify-button">
                    {submitting ? "Verifying…" : "Verify & Sign in"}
                  </Button>
                </form>
              </>
            )}

            <p className="text-xs text-slate-500 mt-6 text-center">GDPR-protected. Customer data encrypted at rest.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
